import format from "date-fns/format";
import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader, ShowMessage } from "app/main/apps/common/ListInfoMessage";
import { Controller, useFormContext } from "react-hook-form";
import {
  Box,
  Card,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import clsx from "clsx";
import { canViewFile, checkNullOrUndefined, handleDownload, onFileViwerClick } from "./AuraFunctions";
import ApprovalContent from "../e-commerce/order/Dialogs/ApprovalContent";
import Attachments from "../e-commerce/shared/Attachments";
import HeaderButton from "./AuraComponents";

/**
 * Background color of messages in the message list
 */
export const getNoteBgColor = (listItem, type) => {
  if (type === "internalNotes") {
    if (listItem?.current_user) {
      return "#D0F0C0"; // Apply green color, If the message is from currently logged-in user
    }
    return "#F3F0FF"; // light purple color
  }
  if (type === "shipmentCommunication") {
    return "#E9F9F9"; // Apply light green color, for the shipment message
  }
  if (listItem?.is_current_user_message || listItem?.current_user) {
    return "#D0F0C0"; // Apply green color, If the message is from currently logged-in user
  }
  let addedType = listItem?.sender_role_name;
  addedType = addedType?.toLowerCase();
  switch (addedType) {
    case "customer":
      return "#E9F2FF"; // Apply light blue color, If the message is from customer
    case "supplier":
      return "#FFECEB"; // Apply light red color, If the message is from supplier
    default:
      return "#F3F0FF"; // Use light purple color, If the message is from for any other employees
  }
};

/**
 * Note/Communication List Item Section
 */
export const communicationListItem = (listItem, index, noteRef, parent) => {
  const isShipmentCommunication = parent === "shipmentCommunication";
  const isCommunication =
    parent === "shipmentCommunication" || parent === "communication";

  const noteDocuments = isCommunication
    ? listItem?.note_documents
    : listItem?.internal_note_documents;
  return (
    <div
      key={`key-${index}`}
      ref={noteRef}
      className={`${!isShipmentCommunication && "md:w-2/3"} mb-20 ${
        listItem?.current_user && "ml-auto"
      }`}
    >
      <div className="font-semibold">
        {isCommunication ? listItem?.sender_user_name : listItem?.added_by}
      </div>
      <Card
        className={clsx(
          "relative w-full rounded-md p-10 min-h-36 shadow space-x-8 mt-8"
        )}
        elevation={0}
        sx={{ background: getNoteBgColor(listItem, parent) }}
      >
        <div className="whitespace-pre-wrap">
          {isCommunication ? listItem?.message : listItem?.note}
        </div>
        <div className="flex flex-wrap">
          {noteDocuments?.length > 0 &&
            noteDocuments?.map((doc) => {
              return (
                <div
                  key={doc.id || doc.filename}
                  className="m-5 py-4 px-8 text-13 bg-white shadow-md rounded-lg font-medium min-w-[10rem]"
                >
                  <Typography className="truncate" title={doc.filename}>
                    {doc.filename}
                  </Typography>
                  {/* PGSS-5550: View + Download like Attachments (PDFs/images), not download-only */}
                  <div className="flex flex-row items-center space-x-12 mt-6 border-t pt-4">
                    {canViewFile(doc) && (
                      <Typography
                        variant="body2"
                        color="secondary"
                        className="cursor-pointer font-medium hover:font-bold"
                        onClick={() => onFileViwerClick(doc)}
                      >
                        View
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="secondary"
                      className="cursor-pointer font-medium hover:font-bold"
                      onClick={() => handleDownload(doc)}
                    >
                      Download
                    </Typography>
                  </div>
                </div>
              );
            })}
        </div>
      </Card>
      <div className="text-xs text-slate-400 font-light ml-4 text-end mt-8">
        {listItem?.added_on &&
          format(new Date(listItem?.added_on), "MMM dd, yyyy h:mm a")}
        {listItem?.created_at &&
          format(new Date(listItem?.created_at), "MMM dd, yyyy h:mm a")}
      </div>
    </div>
  );
};

/**
 * Versatile component designed for communication-related structures.
 * Manages both note history and communication logs.
 * Currently utilized in:
 * 1. Internal Notes History section
 * 2. Shipment Communication History
 */
function AuraCommunication(props) {
  const {
    parent = "internalNotes",
    showHeader = false,
    showNotesHistory = true,
    showEmptyNotesHistoryMsg = false,
    showAddNote = true,
    emptyListMsg = "No Notes Found!",
    messageLabel = "Add your internal note",
  } = props;
  const methods = useFormContext();
  const { control, watch, getValues } = methods;
  const [approvalDialog, setApprovalDialog] = useState({});
  const noteRefs = useRef({});
  const lastNoteRef = useRef(null);
  const formData = getValues();
  const [searchParams] = useSearchParams();
  const isComplete = searchParams.get("is_complete");
  const messageId = searchParams.get("mid");
  const [hasScrolledToMessage, setHasScrolledToMessage] = useState(false);
  const newNoteAttachments = formData?.new_internal_note_attachments || [];
  const note = watch("note");

  /**
   * Props for Add attachment popup
   */
  const attachmentProperties = {
    type: "addAttachments",
    title: "Attachments",
    open: true,
    attachmentProps: {
      newAttachmentsType: `new_internal_note_attachments`,
      width: "w-[12rem]",
      height: "h-[9rem]",
      fontSize: "text-12",
    },
  };

  /**
   * Props for View/edit attachments before sending the message
   */
  const editAttachmentProperties = {
    category: "document",
    allowAdd: false,
    allowDelete: true,
    attachmentKey: "addAttachments",
    ...attachmentProperties.attachmentProps,
  };

  useLayoutEffect(() => {
    if (!props?.listItems?.length) return;

    // If messageId exists and hasn't been scrolled to yet
    if (messageId && !hasScrolledToMessage && noteRefs.current[messageId]) {
      noteRefs.current[messageId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setHasScrolledToMessage(true); // prevent re-scrolling on future list updates
    }
    // If messageId not present or already handled, scroll to the last note
    else if (!messageId || hasScrolledToMessage) {
      lastNoteRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [props?.listItems, messageId]);

  useEffect(() => {
    return () => setHasScrolledToMessage(false); // cleanup
  }, [messageId]);

  /**
   * On Message Send
   */
  const isParentType = (type) => {
    return parent === type;
  };

  /**
   * On Message Send
   */
  const handlePostMessage = () => {
    props.handlePostMessage();
  };

  /**
   * handle enable/disable message send button
   */
  const disableSendButton = () => {
    return (
      ((!note || note && note?.length === 0) && newNoteAttachments?.length === 0) ||
      props?.loading === true ||
      props?.submiting === true
    );
  };

  /**
   * handle enable/disable message Mark As Complete button
   */
  const disableMarkAsComplete = () => {
    return props?.loading === true || props?.submiting === true;
  };


  /**
   * Communication box height set up
   */
  const getBoxHeight = () => {
    return newNoteAttachments?.length > 0
      ? "h-[35vh] max-h-[35vh]"
      : "h-[60vh] max-h-[60vh]";
  };

  return (
    <>
      <Paper
        variant="outlined"
        className={`border border-solid ${
          isParentType("internalNotes")
            ? "rounded-sm !border-grey"
            : "rounded-md"
        }`}
      >
        {showHeader && isParentType("shipmentCommunication") && (
          <div className="flex flex-row sticky bg-[#e9eef9] p-12 justify-between items-center">
            <Typography className="font-bold">
              Communication Histories
            </Typography>
            {isComplete && (
              <div className="text-center ml-auto">
                <HeaderButton
                  size="small"
                  className="whitespace-nowrap mx-4"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    props.handleMarkAsCompleted();
                  }}
                  disabled={disableMarkAsComplete()}
                >
                  Mark as Completed
                </HeaderButton>
              </div>
            )}
          </div>
        )}
        <div className="p-12">
          <Paper
            variant="outlined"
            className={`mb-10 rounded-md border-none overflow-y-auto ${getBoxHeight()}`}
          >
            {showNotesHistory && <>
              {props?.loading === true &&
                <div className="flex flex-1 w-full justify-center">
                  <Loader style={{ height: "60vh", width: "100%" }} />
                </div>
              }
              {(checkNullOrUndefined(props?.loading) || props?.loading === false) &&
                (props?.listItems && props?.listItems?.length > 0
                  ? props?.listItems?.map((item, index) => {
                      const isLastItem = index === props.listItems?.length - 1;
                      return communicationListItem(
                        item,
                        index,
                        (el) => {
                          noteRefs.current[item.message_id] = el;
                          if (isLastItem) lastNoteRef.current = el;
                        },
                        parent
                      );
                    })
                  : showEmptyNotesHistoryMsg && (
                      <div
                        style={{
                          height:
                            newNoteAttachments?.length > 0 ? "35vh" : "60vh",
                          width: "100%",
                        }}
                      >
                        <ShowMessage message={emptyListMsg} />
                      </div>
                    ))}
            </>}
          </Paper>
          {showAddNote && (
            <div className="sticky bottom-0">
              <div className="mt-12">
                <Controller
                  control={control}
                  name="note"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="pr-0"
                      label={messageLabel}
                      id="note"
                      variant="outlined"
                      size="medium"
                      multiline
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <Box>
                              <Tooltip title="Attach Documents">
                                <IconButton
                                  onClick={() => {
                                    setApprovalDialog(attachmentProperties);
                                  }}
                                  className="rounded-sm p-4 mr-8 hover:bg-gray-300"
                                >
                                  <FuseSvgIcon
                                    className="rounded-sm text-48 cursor-pointer text-green-600"
                                    size={22}
                                  >
                                    material-solid:attach_file
                                  </FuseSvgIcon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send Note">
                                <IconButton
                                  className={`rounded-sm p-4 ${
                                    !disableSendButton()
                                      ? "bg-green text-white hover:bg-green-800"
                                      : "bg-[#eee] text-gray-400"
                                  }`}
                                  onClick={handlePostMessage}
                                  disabled={disableSendButton()}
                                >
                                  <FuseSvgIcon
                                    className="text-48 cursor-pointer"
                                    size={22}
                                  >
                                    material-twotone:send
                                  </FuseSvgIcon>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </InputAdornment>
                        ),
                        className: "pr-0",
                      }}
                    />
                  )}
                />
              </div>
              {newNoteAttachments?.length > 0 && (
                <div className="flex">
                  <Attachments {...editAttachmentProperties} />
                </div>
              )}
            </div>
          )}
        </div>
      </Paper>

      <ApprovalContent
        {...approvalDialog}
        onClose={() => setApprovalDialog({ open: false })}
        heading="Add Documents"
        hideDialogActions
      />
    </>
  );
}

export default AuraCommunication;
